import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsX, BsRobot, BsLightningCharge } from 'react-icons/bs';

interface PostOpportunityModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
}

const PostOpportunityModal: React.FC<PostOpportunityModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        skills: '',
        budget: '',
        duration: '',
        location: 'remote',
        skillLevel: 'expert',
        urgency: 'high'
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-2xl bg-premium-wood-dark border border-premium-gold/30 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 border-b border-white/10 bg-white/5 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-premium-gold/10 to-transparent opacity-50 pointer-events-none" />
                        <h2 className="text-2xl font-serif font-bold text-white relative z-10 flex items-center gap-3">
                            <span className="text-premium-gold">✦</span> Post an Opportunity
                        </h2>
                        <button onClick={onClose} className="text-white/50 hover:text-white transition-colors relative z-10">
                            <BsX size={32} />
                        </button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 max-h-[70vh] overflow-y-auto">

                        <div>
                            <label className="block text-sm font-bold text-white/80 mb-2">Project Title</label>
                            <input
                                type="text"
                                name="title"
                                required
                                placeholder="e.g. Next.js SaaS Platform Redesign"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-premium-gold focus:ring-1 focus:ring-premium-gold outline-none transition-all"
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-white/80 mb-2">Required Skills (Comma separated)</label>
                            <input
                                type="text"
                                name="skills"
                                required
                                placeholder="e.g. React, TypeScript, UI/UX"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-premium-gold focus:ring-1 focus:ring-premium-gold outline-none transition-all"
                                onChange={handleChange}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-white/80 mb-2">Budget (USD)</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">$</span>
                                    <input
                                        type="number"
                                        name="budget"
                                        required
                                        placeholder="5000"
                                        className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-4 py-3 text-white placeholder:text-white/30 focus:border-premium-gold focus:ring-1 focus:ring-premium-gold outline-none transition-all"
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-white/80 mb-2">Expected Duration</label>
                                <select
                                    name="duration"
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-premium-gold focus:ring-1 focus:ring-premium-gold outline-none transition-all appearance-none"
                                    onChange={handleChange}
                                >
                                    <option value="1 week">1 Week</option>
                                    <option value="1 month">1 Month</option>
                                    <option value="3 months">3 Months</option>
                                    <option value="6+ months">6+ Months</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/10 pt-6">
                            <div>
                                <label className="block text-xs font-bold text-white/60 mb-2 uppercase tracking-wide">Location</label>
                                <select name="location" onChange={handleChange} className="w-full bg-black/20 border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:border-premium-gold outline-none">
                                    <option value="remote">🌐 Remote (Global)</option>
                                    <option value="local_zw">🇿🇼 Local (Zimbabwe)</option>
                                    <option value="local_sa">🇿🇦 Local (South Africa)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-white/60 mb-2 uppercase tracking-wide">Skill Level</label>
                                <select name="skillLevel" onChange={handleChange} className="w-full bg-black/20 border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:border-premium-gold outline-none">
                                    <option value="expert">Expert (Top 1%)</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="entry">Entry Level</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-white/60 mb-2 uppercase tracking-wide">Urgency</label>
                                <select name="urgency" onChange={handleChange} className="w-full bg-black/20 border border-white/5 rounded-lg px-3 py-2 text-sm text-white focus:border-premium-gold outline-none">
                                    <option value="high">High (ASAP)</option>
                                    <option value="normal">Normal</option>
                                    <option value="low">Flexible</option>
                                </select>
                            </div>
                        </div>

                        {/* Intelligent Hint */}
                        <div className="bg-premium-gold/10 border border-premium-gold/30 rounded-lg p-4 flex gap-4 mt-6">
                            <BsRobot className="text-premium-gold w-6 h-6 shrink-0 mt-1" />
                            <div>
                                <h4 className="text-sm font-bold text-premium-gold mb-1">AI Talent Matching</h4>
                                <p className="text-xs text-white/60 leading-relaxed">
                                    Upon posting, our AI will instantly scan through verified professionals and populate the Smart Matching panel with your top 5 targeted candidates.
                                </p>
                            </div>
                        </div>

                    </form>

                    {/* Footer */}
                    <div className="p-6 border-t border-white/10 flex justify-end gap-4 bg-black/20">
                        <button onClick={onClose} className="px-6 py-3 font-bold text-white/60 hover:text-white transition-colors">
                            Cancel
                        </button>
                        <button onClick={handleSubmit} className="px-8 py-3 bg-gradient-to-r from-premium-gold-dark via-premium-gold to-premium-gold-light text-black font-bold rounded-lg shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow flex items-center gap-2">
                            <BsLightningCharge /> Post & Match
                        </button>
                    </div>

                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default PostOpportunityModal;
